import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { stringify } from 'query-string';
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useHistory } from 'react-router';
import { upperCase } from 'lodash';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { fetchInfinite, fetcher } from '@dashboard/services/request';
import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import { FormElement, Label, Select } from '@dashboard/components/Form';
import Link from '@dashboard/components/Link';
import Spinner from '@dashboard/components/Spinner';
import Lozenge from '@dashboard/components/Lozenge';
import Dialog from '@dashboard/components/Dialog';
import Button from '@dashboard/components/Button';
import Badge from '@dashboard/components/Badge';
import { useToast } from '@dashboard/context/ToastContext';

import Table from '../components/Table';
import { WEBHOOK_EVENT_TYPES } from '@dashboard/enums/webhook-events';

export default function IntegrationLogs() {
  const { token } = useAuth();
  const { emitToast } = useToast();
  const queryClient = useQueryClient();
  const history = useHistory();
  const query = useUrlQuery();

  const [sortBy, setSortBy] = useState(query.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(query.sortDirection || -1);
  const [status, setStatus] = useState(query.status || 'all');
  const [event, setEvent] = useState(query.event || 'all');
  const [integrationType, setIntegrationType] = useState(query.integrationType || 'all');

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        status,
        event,
        integrationType,
      }),
    });
  }, [sortBy, sortDirection, status, event, integrationType]);

  const { data, fetchNextPage, isLoading, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['integrationLogs', sortBy, sortDirection, status, event, integrationType],
    queryFn: async ({ pageParam }) =>
      fetchInfinite(
        `/admin/integrations/logs?${stringify({
          sort: JSON.stringify({
            [sortBy]: sortDirection,
          }),
          ...(status !== 'all' ? { deliveryStatus: status } : {}),
          ...(event !== 'all' ? { event } : {}),
          ...(integrationType !== 'all' ? { integrationType } : {}),
          limit: 20,
          skip: pageParam * 20,
        })}`,
        token,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
  });

  const resendMutation = useMutation({
    mutationFn: ({ integrationId, integrationLogId }) =>
      fetcher(`/integrations/${integrationId}/logs/${integrationLogId}/resend`, token, {
        method: 'POST',
      }),
    onSuccess: () => {
      emitToast({
        title: 'Integration log resent successfully',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['integrationLogs'] });
    },
    onError: error => {
      emitToast({
        title: 'Failed to resend integration log',
        description: error?.message,
        type: 'error',
      });
    },
  });

  function onSort(newSortBy) {
    const oppositeSortDirection = sortDirection * -1;
    setSortDirection(sortBy === newSortBy ? oppositeSortDirection : -1);
    setSortBy(newSortBy);
  }

  const getStatusVariant = status => {
    switch (status) {
      case 'succeeded':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <div className="pr-4">
      <div className="flex items-end justify-between mb-4">
        <h1>Integration Logs</h1>

        <div className="flex items-end gap-x-4">
          <FormElement className="!mb-0">
            <Label>Integration Type</Label>
            <Select
              value={integrationType}
              onChange={e => setIntegrationType(e.target.value)}
              className="w-40"
            >
              <option value="all">All Types</option>
              <option value="webhook">Webhook</option>
              <option value="googleSheets">Google Sheets</option>
            </Select>
          </FormElement>

          <FormElement className="!mb-0">
            <Label>Event</Label>
            <Select value={event} onChange={e => setEvent(e.target.value)} className="w-40">
              <option value="all">All Events</option>
              <option value={WEBHOOK_EVENT_TYPES.CONVERSION}>Conversion</option>
            </Select>
          </FormElement>

          <FormElement className="!mb-0">
            <Label>Status</Label>
            <Select value={status} onChange={e => setStatus(e.target.value)} className="w-32">
              <option value="all">All Status</option>
              <option value="succeeded">Succeeded</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
            </Select>
          </FormElement>
        </div>
      </div>

      <Table.Table>
        <Table.Head>
          <Table.Th
            onSort={onSort}
            sortKey="sellerId"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Seller
          </Table.Th>
          <Table.Th onSort={onSort} sortKey="pageId" sortBy={sortBy} sortDirection={sortDirection}>
            Page
          </Table.Th>
          <Table.Th
            onSort={onSort}
            sortKey="integrationType"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Type
          </Table.Th>
          <Table.Th onSort={onSort} sortKey="event" sortBy={sortBy} sortDirection={sortDirection}>
            Event
          </Table.Th>
          <Table.Th
            onSort={onSort}
            sortKey="createdAt"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Date
          </Table.Th>
          <Table.Th
            onSort={onSort}
            sortKey="deliveryStatus"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Status
          </Table.Th>
        </Table.Head>

        {!isLoading && (
          <BottomScrollListener onBottom={fetchNextPage} as="tbody">
            {data.pages.map(page =>
              page.data.map(log => (
                <Table.Row key={log._id}>
                  <Table.Td>
                    <Link to={`/admin/merchants/${log.seller?._id}`}>
                      {log.seller?.displayName || log.seller?.name || log.sellerId}
                    </Link>
                  </Table.Td>
                  <Table.Td>
                    <Link to={`/pages/${log.page?._id}`}>{log.page?.name}</Link>
                  </Table.Td>
                  <Table.Td>
                    <div className="flex gap-x-1">
                      <Lozenge variant="info">{upperCase(log.integrationType)}</Lozenge>
                    </div>
                  </Table.Td>
                  <Table.Td>
                    <Lozenge variant="default">{upperCase(log.event)}</Lozenge>
                  </Table.Td>
                  <Table.Td>{format(new Date(log.createdAt), 'yyyy-MM-dd HH:mm')}</Table.Td>
                  <Table.Td>
                    <div className="flex items-center gap-x-2">
                      <Badge appearance={getStatusVariant(log.deliveryStatus)}>
                        {upperCase(log.deliveryStatus)}
                      </Badge>

                      {log.response && log.deliveryStatus === 'failed' && (
                        <Dialog
                          title={`Error for log ID ${log._id}`}
                          trigger={
                            <Button appearance="light" size="small">
                              View error
                            </Button>
                          }
                          confirmButton={null}
                          cancelButton={null}
                        >
                          <div>Status Code: {log.statusCode}</div>
                          <div>Retries: {log.retries}</div>
                          <div className="text-red-600 mt-2">{log.response}</div>

                          <div className="mt-4">
                            <Button
                              appearance="primary"
                              size="small"
                              onClick={() =>
                                resendMutation.mutate({
                                  integrationId: log.integrationId,
                                  integrationLogId: log._id,
                                })
                              }
                              isLoading={resendMutation.isLoading}
                            >
                              Resend Integration
                            </Button>
                          </div>
                        </Dialog>
                      )}
                    </div>
                  </Table.Td>
                </Table.Row>
              )),
            )}
          </BottomScrollListener>
        )}
      </Table.Table>

      {(isFetchingNextPage || isLoading) && <Spinner />}
    </div>
  );
}
